import {
  compact,
  filter,
  flattenDeep,
  forEach,
  get,
  groupBy,
  has,
  isArray,
  isEmpty,
  isEqual,
  isNil,
  isPlainObject,
  isString,
  map,
  reduce,
  toLower,
  trim,
  values,
} from 'lodash';
import { segmentsToString } from '../components/MacroInput/tools/segmentsToString';
import {
  InterSearchFilterOps,
  SearchFilterOps,
  SearchFilterOpTypes,
  SearchFilterTypeAliases,
  SearchFilterTypes,
  SearchFilterValueTypes,
} from '../constants/visibilityFilters';
import { applySyntheticSearchFilters } from 'new/utils/syntheticSearchFilters';
import { getConnectionOrVendorType } from './ResourceUtils';

const filterTypeToPropertyMap = {
  [SearchFilterTypes.ID]: 'id',
  [SearchFilterTypes.NAME]: 'name',
  [SearchFilterTypes.DESCRIPTION]: 'description',
  [SearchFilterTypes.TAG]: 'tags',
  [SearchFilterTypes.STATUS]: 'status',
  [SearchFilterTypes.FLOW_TYPE]: 'flow_type',
  [SearchFilterTypes.SOURCE_ID]: 'data_source',
  [SearchFilterTypes.CONNECTOR_CODE]: 'source_type',
  [SearchFilterTypes.PARENT_DATA_SET_ID]: null,
  [SearchFilterTypes.DESTINATION_ID]: 'data_sink_id',
  [SearchFilterTypes.ATTR_NAME]: null,
  [SearchFilterTypes.API_KEY]: 'api_key',
  [SearchFilterTypes.DISCOVERABLE_DATA]: 'discoverable_data',
};

export function processSearchFilters(filters, availableFilters) {
  let processedFilters = [];
  const filtersByIsString = groupBy(filters, isString);
  const rawStrings = compact(map(filtersByIsString[true], trim));
  let macros = filtersByIsString[false] || [];

  // add default operators to macros without operators
  macros = map(macros, ({ token: field, operator, value }) => ({
    field,
    operator:
      operator || availableFilters[field]?.availableOpTypes[0] || SearchFilterOpTypes.EQUALS,
    // Preserve boolean values, only trim strings (for API compatibility)
    value: typeof value === 'boolean' ? value : trim(value),
  }));

  // remove invalid macros (where operator needs value but no value is set)
  macros = filter(
    macros,
    ({ operator, value }) => SearchFilterOps[operator]?.needsVal === false || value,
  );

  // combine the macros of the same type with OR
  const groupedMacros = groupBy(macros, 'field');

  forEach(groupedMacros, (macros) => {
    if (macros.length === 1) {
      processedFilters.push(macros[0]);
    } else {
      processedFilters.push(['or', ...macros]);
    }
  });

  // build macros from rawStrings (for each possible field, create {field, operator, value})
  const rawStringMacros = getMacrosFromRawStrings(rawStrings, availableFilters);

  if (!isEmpty(rawStringMacros)) {
    processedFilters.push(['or', ...rawStringMacros]);
  }

  // combine all macros with AND
  if (processedFilters.length > 1) {
    processedFilters.unshift('and');
  }

  forEach(filter(flattenDeep(processedFilters, isPlainObject)), (thisFilter) => {
    // replace aliases
    if (SearchFilterTypeAliases[thisFilter.field]) {
      thisFilter.field = SearchFilterTypeAliases[thisFilter.field];
    }
  });

  // translate UI-only keys (archived, is_valid, ...) into the API filters they stand for
  return { processedFilters: applySyntheticSearchFilters(processedFilters) };
}

export function getMacrosFromRawStrings(rawStrings, availableFilters) {
  const rawStringMacros = [];

  forEach(rawStrings, (value) => {
    const isValueInteger = !isNaN(value);
    const valueLowerCase = toLower(value);

    forEach(availableFilters, (filter, filterType) => {
      const filterValueType = SearchFilterValueTypes[filterType];
      const defaultOperator = filter.availableOpTypes[0] || SearchFilterOpTypes.EQUALS;

      switch (filterValueType) {
        case 'string':
          rawStringMacros.push({
            field: filterType,
            operator: defaultOperator,
            value,
          });
          break;

        case 'int':
          if (isValueInteger) {
            rawStringMacros.push({
              field: filterType,
              operator: defaultOperator,
              value,
            });
          }
          break;

        case 'enum':
          forEach(filter.availableValues, (availableValue) => {
            let availableValueString;
            let availableValueLabel;

            if (isString(availableValue)) {
              availableValueString = availableValue;
              availableValueLabel = availableValue;
            } else {
              if (availableValue.matchFreeText === false) {
                return;
              }

              availableValueString = availableValue.value;
              availableValueLabel = availableValue.label;
            }

            if (toLower(availableValueLabel).includes(valueLowerCase)) {
              rawStringMacros.push({
                field: filterType,
                operator: defaultOperator,
                value: availableValueString,
              });
            }
          });
          break;
      }
    });
  });

  return rawStringMacros;
}

export function areSegmentsDifferent(segments1, segments2) {
  return trim(segmentsToString(segments1)) !== trim(segmentsToString(segments2));
}

export function processClientSideSearchFilters(filters, availableFilters) {
  const processedFilters = [];
  const filtersByIsString = groupBy(filters, isString);
  const rawStrings = compact(map(filtersByIsString[true], trim));
  let macros = filtersByIsString[false] || [];

  // add default operators to macros without operators
  macros = map(macros, ({ token: field, operator, value }) => ({
    field,
    operator:
      operator || availableFilters[field]?.availableOpTypes[0] || SearchFilterOpTypes.EQUALS,
    // Preserve boolean values, only trim strings (for API compatibility)
    value: typeof value === 'boolean' ? value : trim(value),
  }));

  // remove invalid macros (where operator needs value but no value is set)
  macros = filter(
    macros,
    ({ operator, value }) => SearchFilterOps[operator]?.needsVal === false || value,
  );

  // build macros from rawStrings (for each possible field, create {field, operator, value})
  const rawStringMacros = getMacrosFromRawStrings(rawStrings, availableFilters);

  // combine the values of macros of the same type
  const groupedMacros = groupBy([...macros, ...rawStringMacros], 'field');

  forEach(groupedMacros, (macros, field) => {
    if (availableFilters[field]) {
      processedFilters.push(cloneAvailableFilter(availableFilters[field], map(macros, 'value')));
    }
  });

  return processedFilters;
}

export function cloneAvailableFilter(availableFilter, filterValues = null) {
  const availableOpTypes = availableFilter.availableOpTypes || values(SearchFilterOpTypes);
  const opType = availableOpTypes[0];
  const filter = {
    ...availableFilter,
    availableOpTypes,
    opType,
    interFilterOp: InterSearchFilterOps.OR,
  };

  if (SearchFilterOps[opType].needsVal !== false) {
    filter.values = filterValues || [''];
  }

  return filter;
}

export function applyFilters(objects, filters, entities, dataSetSchemas, limit = -1) {
  const validFilters = map(getValidFilters(filters), (filter) => ({
    ...filter,
    values: compact(filter.values),
  }));
  const clientSideFilters = filter(validFilters, (filter) =>
    has(filterTypeToPropertyMap, filter.type),
  );
  const serverSideFilters = filter(
    validFilters,
    (filter) => !has(filterTypeToPropertyMap, filter.type),
  );

  function shouldIncludeObject(object) {
    return reduce(
      clientSideFilters,
      (acc, filter, index) =>
        index === 0 || filter.interFilterOp === InterSearchFilterOps.AND
          ? acc && applyFilterOnObject(object, filter, entities, dataSetSchemas)
          : acc || applyFilterOnObject(object, filter, entities, dataSetSchemas),
      true,
    );
  }

  let filteredObjects;
  let filteredObjectsCount = 0;

  if (isArray(objects)) {
    filteredObjects = [];

    forEach(objects, (object) => {
      if (limit > -1 && filteredObjectsCount >= limit) {
        return false;
      }

      if (shouldIncludeObject(object)) {
        filteredObjects.push(object);
        filteredObjectsCount++;
      }
    });
  } else {
    filteredObjects = {};

    forEach(objects, (object, key) => {
      if (limit > -1 && filteredObjectsCount >= limit) {
        return false;
      }

      if (shouldIncludeObject(object)) {
        filteredObjects[key] = object;
        filteredObjectsCount++;
      }
    });
  }

  return {
    filteredObjects,
    serverSideFilters,
  };
}

export function areFiltersDifferent(filters1, filters2) {
  return filters1 !== filters2 && !isEqual(getValidFilters(filters1), getValidFilters(filters2));
}

export function getDataSetPipelineURLParam(dataSetId, dataSet) {
  // add source id filter if possible; else, Nexset id filter
  const sourceId =
    get(dataSet, 'data_source_id') || get(dataSet, 'parent_data_sets[0].data_source_id');
  let filterType;
  let filterValue;

  if (sourceId) {
    filterType = SearchFilterTypes.SOURCE_ID;
    filterValue = sourceId;
  } else {
    filterType = SearchFilterTypes.NEXSET_ID;
    filterValue = dataSetId;
  }

  return `search=${filterType}:${filterValue}`;
}

function applyFilterOnObject(object, thisFilter, entities, dataSetSchemas) {
  const { type, opType, values } = thisFilter;
  const filterValues = map(values, (value) => String(value).toLowerCase());
  let objectValues;

  switch (type) {
    case SearchFilterTypes.PARENT_DATA_SET_ID:
      objectValues = map(object.parent_data_sets, (ds) => ds.id);
      break;

    case SearchFilterTypes.ATTR_NAME:
      const schema = dataSetSchemas[object.id];
      objectValues = map(schema && schema.out, '__name');
      break;

    case SearchFilterTypes.CONNECTOR_CODE:
      {
        const property = filterTypeToPropertyMap[type];
        const { vendors } = entities;

        const connectionOrVendorType = !isNil(object)
          ? getConnectionOrVendorType(object, vendors, property, 'vendor_id')
          : null;
        objectValues = !isNil(connectionOrVendorType) ? [connectionOrVendorType] : [];
      }
      break;

    default: {
      const property = filterTypeToPropertyMap[type];
      const objectValue = get(object, property);
      objectValues = isArray(objectValue) ? objectValue : [objectValue];
    }
  }

  objectValues = map(objectValues, (value) => String(value).toLowerCase());

  if (SearchFilterOps[opType].needsVal === false) {
    return filter(objectValues, (objectValue) => compareValues(objectValue, opType)).length > 0;
  }

  return (
    filter(objectValues, (objectValue) => {
      if (filterValues.length === 0) {
        return compareValues(objectValue, opType);
      }

      for (let i = 0; i < filterValues.length; i++) {
        if (compareValues(objectValue, opType, filterValues[i])) {
          return true;
        }
      }

      return false;
    }).length > 0
  );
}

function compareValues(value1, opType, value2) {
  switch (opType) {
    case SearchFilterOpTypes.EQUALS:
      return value1 === value2;

    case SearchFilterOpTypes.NOT_EQUALS:
      return value1 !== value2;

    case SearchFilterOpTypes.CONTAINS:
      return value1.includes(value2);

    case SearchFilterOpTypes.NOT_CONTAINS:
      return !value1.includes(value2);

    case SearchFilterOpTypes.STARTS_WITH:
      return value1.startsWith(value2);

    case SearchFilterOpTypes.ENDS_WITH:
      return value1.endsWith(value2);

    case SearchFilterOpTypes.GREATER_THAN:
      return parseInt(value1, 10) > parseInt(value2, 10);

    case SearchFilterOpTypes.LESS_THAN:
      return parseInt(value1, 10) < parseInt(value2, 10);

    case SearchFilterOpTypes.IS_EMPTY:
      return isEmpty(value1);

    case SearchFilterOpTypes.IS_NOT_EMPTY:
      return !isEmpty(value1);

    default:
  }

  return true;
}

function getValidFilters(filters) {
  return filter(
    filters,
    (filter) =>
      SearchFilterOps[filter.opType]?.needsVal === false || compact(filter.values).length > 0,
  );
}
